import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

interface ScoreBreakdownProps {
  scores: {
    momentum: number;
    position: number;
    technical: number;
    fundamentals: number;
    sentiment: number;
    risk: number;
  };
  positionDetails?: {
    supplyValueHealth?: {
      fdvRatioScore?: number;
      supplyCirculationScore?: number;
      athRecoveryScore?: number;
      undervaluationScore?: number;
    };
  };
  rating?: string;
}

export const ScoreBreakdown = ({ scores, positionDetails, rating }: ScoreBreakdownProps) => {
  const supplyHealth = positionDetails?.supplyValueHealth;
  const scoreItems = [
    { label: "Momentum & Volume", value: scores.momentum, max: 30, color: "hsl(var(--primary))" },
    { label: "Position & Market Cap", value: scores.position, max: 20, color: "hsl(var(--secondary))" },
    { label: "Technical Indicators", value: scores.technical, max: 20, color: "hsl(var(--accent))" },
    { label: "Fundamentals", value: scores.fundamentals, max: 15, color: "hsl(var(--chart-1))" },
    { label: "Social Sentiment", value: scores.sentiment, max: 10, color: "hsl(var(--chart-2))" },
    { label: "Risk Score", value: scores.risk, max: 5, color: "hsl(var(--chart-3))" },
  ];

  const totalScore = scoreItems.reduce((acc, item) => acc + item.value, 0);
  
  const getRatingBadge = (rating?: string) => {
    if (!rating) return null;
    const badges = {
      'HOT_PICK': 'bg-green-500/20 text-green-400 border-green-500/30',
      'STRONG_CONTENDER': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'WATCHLIST': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'SKIP': 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return badges[rating as keyof typeof badges] || badges.WATCHLIST;
  };

  return (
    <Card className="p-6 gradient-card border-border/50 shadow-elevation">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-foreground">Token Score Breakdown</h3>
        {rating && (
          <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getRatingBadge(rating)}`}>
            {rating.replace('_', ' ')}
          </div>
        )}
      </div>
      <div className="space-y-4">
        {scoreItems.map((item, idx) => (
          <div key={idx} className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">{item.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">max {item.max}</span>
                <span className="font-semibold font-mono text-foreground">{item.value.toFixed(1)}</span>
              </div>
            </div>
            <Progress value={(item.value / item.max) * 100} className="h-1.5" />
            {item.label === "Position & Market Cap" && supplyHealth && (
              <details className="rounded-md border border-border/40 bg-muted/20 px-3 py-2">
                <summary className="cursor-pointer text-xs font-medium text-foreground">
                  Supply & Value Health
                </summary>
                <div className="mt-3 space-y-3">
                  {[
                    { label: "FDV Ratio", value: supplyHealth.fdvRatioScore ?? 5, tooltip: "Percentage of total supply already in circulation. Higher means less future dilution risk." },
                    { label: "Supply Circulation", value: supplyHealth.supplyCirculationScore ?? 5, tooltip: "How much of the maximum token supply is currently circulating." },
                    { label: "ATH Recovery Signal", value: supplyHealth.athRecoveryScore ?? 5, tooltip: "Tokens significantly below all-time high with rising volume may be in early repricing phase." },
                  ].map((subSignal) => (
                    <div key={subSignal.label} className="space-y-1.5" title={subSignal.tooltip}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{subSignal.label}</span>
                        <span className="font-mono font-semibold text-foreground">{subSignal.value.toFixed(1)}/10</span>
                      </div>
                      <Progress value={(subSignal.value / 10) * 100} className="h-1" />
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        ))}
        <div className="pt-4 border-t border-border/30">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-foreground">Total Score</span>
            <span className="text-2xl font-bold font-mono text-primary">{totalScore.toFixed(1)}/100</span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
            Base score is capped at 100. A separate Social Boost of up to +15 can be added on top when narrative and social-volume divergences fire — so a token's total TOSS can exceed 100.
          </p>
        </div>
      </div>
    </Card>
  );
};
